import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  FormControl,
  Grid,
  InputLabel,
  ListSubheader,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { variants } from '../../../../variants/variants';
import {
  useAddNominationToCategoryMutation,
  useGetActiveQuarterQuery,
  useGetAllEmployeesQuery,
} from '../../rnrApiSlice';
import { useDispatch, useSelector } from 'react-redux';
import { selectCurrentUserEmpCode } from '../../../Auth/authSlice';
import { useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import FileUpload from '../../../../common/Utility/FileUpload';
import FormSkeleton from '../../skeleton/FormSkeleton';
import useDetectKeyboardOpen from 'use-detect-keyboard-open';
import { selectUsers, toggleSwipe } from '../../../Search/searchSlice';
import UserAvatar from '../../../../common/UserAvatar';
import { set } from 'lodash';
import AttachmentNameDisplay from '../../../../common/Utility/AttachmentNameDisplay';

// Validation schema for the formik input fields
const validationSchema = Yup.object().shape({
  categories: Yup.string().required('Category is required'),
  description: Yup.string()
    .min(10, 'Description must be at least 10 characters long')
    .max(500, 'Description must be at most 500 characters long')
    .required('Description is required'),
  teamMembers: Yup.array(),
  teamName: Yup.string().when('categories', {
    is: 'Team Work',
    then: (schema) => schema.required('Team name is required'),
  }),
  attachment: Yup.mixed().notRequired(),
});

const RnRForm = () => {
  const { data: employeeData, isLoading, error } = useGetAllEmployeesQuery();
  const { data: currentQuarter } = useGetActiveQuarterQuery();
  const [addNominationToCategory] = useAddNominationToCategoryMutation();
  const currUserEmpCode = useSelector(selectCurrentUserEmpCode);
  const { empCode } = useParams();
  const nominateButtonRef = useRef(null);
  const isKeyboardOpen = useDetectKeyboardOpen();
  const [isAttached, setIsAttached] = useState(false);
  const theme = useTheme();
  const [searchTerm, setSearchTerm] = useState('');
  const [searchText, setSearchText] = useState('');
  const dispatch = useDispatch();

  const isSm = useMediaQuery(theme.breakpoints.down('sm'));
  const scrollToBottom = () => {
    if (nominateButtonRef.current) {
      nominateButtonRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  };
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      setSearchTerm(searchText);
    }, 300); // Adjust delay (ms) as needed

    return () => clearTimeout(delayDebounce); // Clean up on next keystroke
  }, [searchText]);
  useEffect(() => {
    window.addEventListener('resize', scrollToBottom);
    return () => window.removeEventListener('resize', scrollToBottom);
  }, []);
  // // Scroll to the Nominate button when the keyboard is open
  // useEffect(() => {
  //   if (isKeyboardOpen && nominateButtonRef.current) {
  //     const element = nominateButtonRef.current;
  //     element.scrollIntoView({ behavior: 'smooth', block: 'end' });
  //   }
  // }, [isKeyboardOpen]);

  const [initialValues, setInitialValues] = useState({
    rnrQuarter: '',
    categories: '',
    description: '',
    teamMembers: [],
    teamName: '',
    attachment: [],
  });

  useEffect(() => {
    if (currentQuarter && currentQuarter.data) {
      setInitialValues({
        rnrQuarter: `Year ${currentQuarter?.data?.time.year}- Q${currentQuarter?.data?.time.quarter}`,
        categories: '',
        description: '',
        teamMembers: [],
        teamName: '',
        attachment: [],
        personName: [],
      });
    }
  }, [currentQuarter]);

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values, { resetForm }) => {
      const formData = new FormData();
      formData.append('quarter', currentQuarter?.data?.time.quarter);
      formData.append('year', currentQuarter?.data?.time.year);

      const selectedEmp = employeeData.find((emp) => emp.userName === values.personName);
      const nominationData = {
        nominationReason: values.description,
        nominatorEmpCode: currUserEmpCode,
      };

      if (values.categories !== 'Team Work') {
        nominationData.nomineeEmpCode = selectedEmp?.empCode;
      } else {
        nominationData.team = [
          {
            teamName: values.teamName,
            teamMembers: values.teamMembers
              .map((userName) => {
                const emp = employeeData.find((emp) => emp.userName === userName);
                return emp?.empCode; // Returns empCode or undefined if not found
              })
              .filter(Boolean),
          },
        ];
      }

      formData.append('nominationData', JSON.stringify(nominationData));
      formData.append('categoryName', values.categories);
      values.attachment.forEach((file) => {
        formData.append('attachments', file);
      });
      dispatch(toggleSwipe({ value: false }));
      await toast.promise(addNominationToCategory(formData).unwrap(), {
        loading: 'Nominating...',
        success: 'Nomination successful!',
        error: (err) => err.data?.message || 'Error in nominating for RnR, Please try again.',
      });

      resetForm();
    },
  });

  const handleFileChange = (event) => {
    const files = Array.from(event.currentTarget.files);
    if(formik.values.attachment.length + files.length > 20){
      toast.error('You can upload maximum 20 files');
      return;
    }
    if(files.length > 0 && files[0]?.size <= 20*1024*1024 ){      
      formik.setFieldValue('attachment', files);
    }
    else{
      toast.error('File size should be less than 20MB');
    }
  };

  const takePicture = async () => {
    try {
      window['ReactNativeWebView'] &&
        window['ReactNativeWebView']?.postMessage(JSON.stringify({ type: 'uploadShabashFile', value: true, data:{image:20,selectionLimit:5,validTypes:['image/jpeg', 'image/jpg', 'image/png']}}));
    } catch (error) {
      console.error('Camera error:', error);
    }
  };

  const base64ToBlob = (base64, type = 'application/octet-stream') => {
    try {
      const base64WithoutPrefix = base64.startsWith('data:') ? base64.substring(base64.indexOf(',') + 1) : base64;
      const byteCharacters = atob(base64WithoutPrefix);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      return new Blob([byteArray], { type });
    } catch (e) {
      console.error('Error converting base64 to Blob:', e);
      console.error('Problematic base64 snippet:', base64.substring(0, 100) + '...');
      return null;
    }
  };

  useEffect(() => {
    // Handle messages from React Native
    const handleMessage = (event) => {
      try {
        let message = event.data;
        if (message?.type === 'uploadedShabashFile') {
          console.log('uploadedShabashFile', message.data);
          const newFileObjects = [];
          message.data.forEach((asset) => {
            if (!asset.base64 || !asset.fileName || !asset.type) {
              console.warn('Asset is missing required fields (base64, fileName, or type):', asset);
              return; // Skip this asset
            }
            const blob = base64ToBlob(asset.base64, asset.type);
            if (blob) {
              const file = new File([blob], asset.fileName, {
                type: asset.type,
                lastModified: asset.timestamp ? new Date(asset.timestamp).getTime() : Date.now(),
              });
              newFileObjects.push(file);
            } else {
              console.warn('Could not convert asset to blob:', asset.fileName);
            }
          });
          formik.setFieldValue('attachment', newFileObjects);
          setIsAttached(true);
          window['ReactNativeWebView'] &&
            window['ReactNativeWebView']?.postMessage(
              JSON.stringify({
                type: 'uploadedShabashFile',
                value: 'uploadedShabashFile received',
              })
            );
        }
      } catch (err) {
        window['ReactNativeWebView'] &&
          window['ReactNativeWebView']?.postMessage(
            JSON.stringify({
              type: 'error',
              value: event.data,
              message: err,
            })
          );
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  // const filteredEmployees = useMemo(() => {
  //   if (!searchTerm) return employeeData;

  //   return employeeData.filter((emp) => {
  //     const fullName = `${emp.firstName} ${emp.middleName} ${emp.lastName}`.toLowerCase();
  //     return fullName.includes(searchTerm.toLowerCase());
  //   });
  // }, [employeeData, searchTerm]);
  const filteredEmployees = useMemo(() => {
    if (!employeeData?.length) return [];

    // Remove duplicates
    const unique = employeeData.filter((emp, index, self) => index === self.findIndex((e) => e._id === emp._id));

    if (!searchText.trim()) return unique.slice(0, 50); // Show only first 50 initially

    const searchLower = searchText.toLowerCase();
    return unique
      .filter((emp) => {
        const searchString = `${emp.firstName || ''} ${emp.middleName || ''} ${emp.lastName || ''} ${
          emp.userName || ''
        } ${emp.department || ''}`.toLowerCase();
        return searchString.includes(searchLower);
      })
      .slice(0, 30); // Limit search results
  }, [employeeData, searchText]);

  const handleSearchChange = useCallback((e) => {
    setSearchText(e.target.value);
  }, []);

  // Add this new handler to prevent Select's default keyboard behavior
  const handleSearchKeyDown = useCallback((e) => {
    // Prevent Select component from handling these keys
    e.stopPropagation();

    // Allow normal text input behavior
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter') {
      e.preventDefault();
    }
  }, []);

  const handleDelete = (e, userNameToDelete) => {
    const updatedMembers = formik.values.teamMembers.filter((userName) => userName !== userNameToDelete);
    formik.setFieldValue('teamMembers', updatedMembers);
  };
  if (isLoading) {
    return <FormSkeleton />;
  }

  if (error) return <p>Error loading active quarter</p>;

  // const filteredEmployees = employeeData.filter((emp) => {
  //   const fullName = `${emp.firstName} ${emp.middleName} ${emp.lastName}`.toLowerCase();
  //   return fullName.includes(searchTerm.toLowerCase());
  // });

  return (
    <div style={{ paddingTop: 0, maxWidth: '720px', marginTop: 0, height: '100%' }}>
      <div style={{}}>
        <form onSubmit={formik.handleSubmit}>
          <Grid container>
            <Grid item xs={12}>
              <Typography sx={{ fontSize: '12px', float: 'right', textAlign: 'right', color: 'grey' }}>
                {formik.values.rnrQuarter}
              </Typography>
            </Grid>
            {/* <Grid item xs={12}>
            <Autocomplete
              multiple
              limitTags={2}
              id="select-members"
              options={employeeData}
              getOptionLabel={(option) => `${option.firstName+" "+option.middleName+" "+option.lastName}`}
              renderOption={(props, option, { selected }) => (
                <li
                // key={option._id}
                  {...props}
                  style={{ display: 'flex', alignItems: 'center', padding: '8px 12px', borderBottom: '1px solid #ddd' }}
                >
                  <UserAvatar size={35} src={option?.employeePhotoPath} />
                  <div style={{ marginLeft: 12 }}>
                    <Typography variant="subtitle1">{option.firstName+" "+option.middleName+" "+option.lastName}</Typography>
                    <Typography variant="body2" style={{ fontSize: 12, color: '#666', lineHeight: 1.2 }}>
                      {option.department}
                    </Typography>
                  </div>
                </li>
              )}
              value={formik.values?.personName?.map((name) =>
                employeeData.find((employee) => employee.userName === name)
              )}
              onChange={(event, newSelected) => {
                const selectedNames = newSelected.map((option) => option.userName);
                formik.setFieldValue('personName', selectedNames);
                formik.setFieldValue('numberOfMembers', selectedNames.length);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Members*"
                  placeholder="Members"
                  fullWidth
                  InputLabelProps={{ style: { color: '#666' } }}
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                  key={index}
                    avatar={<UserAvatar size={24} src={option?.employeePhotoPath} />}
                    label={option.userName}
                    {...getTagProps({ index })}
                  />
                ))
              }
            />
            {formik.touched.personName && formik.errors.personName && (
              <Typography variant="body2" color="error">
                {formik.errors.personName}
              </Typography>
            )}
          </Grid> */}
            {/* <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id="select-members-label">Select Member*</InputLabel>
                <Select
                  labelId="select-members-label"
                  id="select-members"
                  value={formik.values.personName}
                  // onChange={(event) => {
                  //   const selectedUserName = event.target.value;
                  //   formik.setFieldValue('personName', selectedUserName);
                  //   formik.setFieldValue('numberOfMembers', 1); // Always 1 for single select
                  // }}
                  onChange={(event) => {
                    const selectedUserName = event.target.value;
                    const selectedEmp = employeeData.find((emp) => emp.userName === selectedUserName);

                    formik.setFieldValue('personName', selectedUserName);
                    formik.setFieldValue('selectedEmpCode', selectedEmp?.empCode || '');
                    formik.setFieldValue('numberOfMembers', 1);
                    // If you want to populate team info here, you can do it too
                    // formik.setFieldValue('teamMembers', selectedEmp.teamMembers || []);
                    setSearchText('');
                    setSearchTerm('');
                  }}
                  input={<OutlinedInput label="Select Member*" />}
                  renderValue={(selected) => {
                    const employee = employeeData.find((emp) => emp.userName === selected);
                    return (
                      <Chip
                        key={selected}
                        label={selected}
                        avatar={<UserAvatar size={24} src={employee?.employeePhotoPath} />}
                      />
                    );
                  }}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 300,
                        overflowY: 'auto',
                      },
                    },
                  }}
                >
                  <ListSubheader>
                    <TextField
                      placeholder="Search member..."
                      fullWidth
                      value={searchText}
                      onChange={(e) => setSearchText(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key !== 'Escape') {
                          e.stopPropagation();
                        }
                      }}
                      onClick={(e) => e.stopPropagation()}
                      size="small"
                      autoFocus
                    />
                  </ListSubheader>

                  {filteredEmployees.map((option) => (
                    <MenuItem
                      key={option.userName}
                      value={option.userName}
                      dense
                      style={{ paddingTop: 4, paddingBottom: 4 }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <UserAvatar size={28} src={option?.employeePhotoPath} />
                        <div style={{ marginLeft: 8 }}>
                          <Typography variant="body2" style={{ fontSize: 13, fontWeight: 500 }}>
                            {`${option.firstName} ${option.middleName} ${option.lastName}`}
                          </Typography>
                          <Typography variant="caption" style={{ fontSize: 11, color: '#666' }}>
                            {option.department}
                          </Typography>
                        </div>
                      </div>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {formik.touched.personName && formik.errors.personName && (
                <Typography variant="body2" color="error">
                  {formik.errors.personName}
                </Typography>
              )}
            </Grid> */}
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id="select-members-label">Select Member*</InputLabel>
                <Select
                  labelId="select-members-label"
                  id="select-members"
                  value={formik.values.personName || ''}
                  onChange={(event) => {
                    const selectedUserName = event.target.value;
                    const selectedEmp = employeeData.find((emp) => emp.userName === selectedUserName);

                    formik.setFieldValue('personName', selectedUserName);
                    formik.setFieldValue('selectedEmpCode', selectedEmp?.empCode || '');
                    formik.setFieldValue('numberOfMembers', 1);
                    setSearchText('');
                  }}
                  input={<OutlinedInput label="Select Member*" />}
                  // Add these props to prevent auto-selection
                  autoComplete="off"
                  disableAutoFocus
                  renderValue={(selected) => {
                    if (!selected) return '';
                    const employee = employeeData?.find((emp) => emp.userName === selected);
                    return (
                      <Chip
                        key={selected}
                        label={selected}
                        avatar={<UserAvatar size={24} src={employee?.employeePhotoPath} />}
                      />
                    );
                  }}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 350,
                        overflowY: 'auto',
                      },
                    },
                    // // Prevent menu from auto-focusing on items
                    // disableAutoFocusItem: true,
                    // // Prevent menu from restoring focus
                    // disableRestoreFocus: true,
                  }}
                >
                  {/* <ListSubheader> */}
                    <TextField
                      placeholder="Search member..."
                      fullWidth
                      value={searchText}
                      onChange={handleSearchChange}
                      onKeyDown={handleSearchKeyDown} // Use the new handler
                      onClick={(e) => e.stopPropagation()}
                      size="small"
                      autoFocus
                      // Add these props for better focus management
                      InputProps={{
                        autoComplete: 'off',
                      }}
                    />
                  {/* </ListSubheader> */}

                  {filteredEmployees.map((option) => (
                    <MenuItem
                      key={option._id || option.userName}
                      value={option.userName}
                      dense
                      style={{ paddingTop: 4, paddingBottom: 4 }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <UserAvatar size={28} src={option?.employeePhotoPath} />
                        <Box sx={{ marginLeft: 1 }}>
                          <Typography variant="body2" sx={{ fontSize: 13, fontWeight: 500 }}>
                            {`${option.firstName || ''} ${option.middleName || ''} ${option.lastName || ''}`.trim()}
                          </Typography>
                          <Typography variant="caption" sx={{ fontSize: 11, color: '#666' }}>
                            {option.department}
                          </Typography>
                        </Box>
                      </Box>
                    </MenuItem>
                  ))}

                  {filteredEmployees.length === 0 && (
                    <MenuItem disabled>
                      <Typography variant="body2" color="textSecondary">
                        No employees found
                      </Typography>
                    </MenuItem>
                  )}
                </Select>
              </FormControl>

              {formik.touched.personName && formik.errors.personName && (
                <Typography variant="body2" color="error">
                  {formik.errors.personName}
                </Typography>
              )}
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Select Category *"
                id="categories"
                name="categories"
                value={formik.values.categories}
                onChange={formik.handleChange}
                select
                margin="dense"
                error={formik.touched.categories && !!formik.errors.categories}
                helperText={formik.touched.categories && formik.errors.categories}
                variant="outlined"
                InputLabelProps={{ style: { fontSize: variants.fontSize.SUB_HEADING } }}
                InputProps={{ style: { fontSize: variants.fontSize.SUB_HEADING } }}
              >
                {currentQuarter?.data?.categoriesSelection?.map((option) => (
                  <MenuItem key={option} value={option} sx={{ fontSize: variants.fontSize.SUB_HEADING }}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            {formik.values.categories === 'Team Work' && (
              <>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Team Name *"
                    name="teamName"
                    type="text"
                    variant="outlined"
                    value={formik.values.teamName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.teamName && !!formik.errors.teamName}
                    helperText={formik.touched.teamName && formik.errors.teamName}
                    margin="dense"
                    InputLabelProps={{ style: { fontSize: variants.fontSize.SUB_HEADING } }}
                    InputProps={{ style: { fontSize: variants.fontSize.SUB_HEADING } }}
                  />
                </Grid>
                {/* <Grid item xs={12}>                  
                  <Autocomplete
                    multiple
                    limitTags={1}
                    options={employeeData}
                    getOptionLabel={(option) => `${option.firstName} ${option.lastName}`}
                    onChange={(event, value) => formik.setFieldValue('teamMembers', value)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        margin="dense"
                        label="Select Team Members *"
                        variant="outlined"
                        error={formik.touched.teamMembers && !!formik.errors.teamMembers}
                        helperText={formik.touched.teamMembers && formik.errors.teamMembers}
                        InputLabelProps={{ style: { fontSize: variants.fontSize.SUB_HEADING } }}
                        InputProps={{ ...params.InputProps, style: { fontSize: variants.fontSize.SUB_HEADING } }}
                      />
                    )}
                  />
                </Grid> */}
                {/* <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel id="select-members-label">Select Team Member*</InputLabel>
                    <Select
                      labelId="select-members-label"
                      id="select-members"
                      value={formik.values.teamMembers}
                      multiple
                      // onChange={(event) => {
                      //   const selectedUserName = event.target.value;
                      //   formik.setFieldValue('personName', selectedUserName);
                      //   formik.setFieldValue('numberOfMembers', 1); // Always 1 for single select
                      // }}
                      onChange={(event) => {
                        const selectedUserName = event.target.value;
                        // const selectedEmp = employeeData.filter((emp) => emp.userName === selectedUserName[0]);
                        formik.setFieldValue('teamMembers', selectedUserName);
                        // If you want to populate team info here, you can do it too
                        // formik.setFieldValue('teamMembers', selectedEmp.teamMembers || []);
                        setSearchText('');
                        setSearchTerm('');
                      }}
                      input={<OutlinedInput label="Select Team Member*" />}
                      // renderValue={(selected) => {
                      //   // const employee = employeeData.find((emp) => emp.userName === selected);
                      //   // return (
                      //   //   <Chip
                      //   //     key={selected}
                      //   //     label={selected}
                      //   //     avatar={<UserAvatar size={24} src={employee?.employeePhotoPath} />}
                      //   //   />
                      //   // );
                      //   {console.log('selected', selected)}
                      //   <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      //     {selected.map((userName) => {
                      //       const employee = employeeData.find((emp) => emp.userName === userName);
                      //       return (
                      //         <Chip
                      //           key={userName}
                      //           label={userName}
                      //           avatar={<UserAvatar size={24} src={employee?.employeePhotoPath} />}
                      //         />
                      //       );
                      //     })}
                      //   </div>;
                      // }}
                      renderValue={(selected) => {
                        return (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                            {selected.map((userName) => {
                              const employee = employeeData.find((emp) => emp.userName === userName);
                              return (
                                <Chip
                                  key={userName}
                                  label={userName}
                                  avatar={<UserAvatar size={24} src={employee?.employeePhotoPath} />}
                                  style={{ margin: 2 }}
                                />
                              );
                            })}
                          </div>
                        );
                      }}
                      MenuProps={{
                        PaperProps: {
                          style: {
                            maxHeight: 300,
                            overflowY: 'auto',
                          },
                        },
                      }}
                    >
                      <ListSubheader>
                        <TextField
                          placeholder="Search Team member..."
                          fullWidth
                          value={searchText}
                          onChange={(e) => setSearchText(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key !== 'Escape') {
                              e.stopPropagation();
                            }
                          }}
                          onClick={(e) => e.stopPropagation()}
                          size="small"
                          autoFocus
                        />
                      </ListSubheader>

                      {filteredEmployees.map((option) => (
                        <MenuItem
                          key={option.userName}
                          value={option.userName}
                          dense
                          style={{ paddingTop: 4, paddingBottom: 4 }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            <UserAvatar size={28} src={option?.employeePhotoPath} />
                            <div style={{ marginLeft: 8 }}>
                              <Typography variant="body2" style={{ fontSize: 13, fontWeight: 500 }}>
                                {`${option.firstName} ${option.middleName} ${option.lastName}`}
                              </Typography>
                              <Typography variant="caption" style={{ fontSize: 11, color: '#666' }}>
                                {option.department}
                              </Typography>
                            </div>
                          </div>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {formik.touched.personName && formik.errors.personName && (
                    <Typography variant="body2" color="error">
                      {formik.errors.teamMembers}
                    </Typography>
                  )}
                </Grid> */}
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel id="select-members-label">Select Team Member*</InputLabel>
                    <Select
                      labelId="select-members-label"
                      id="select-members"
                      value={formik.values.teamMembers}
                      multiple
                      onChange={(event) => {
                        const selectedUserName = event.target.value;
                        formik.setFieldValue('teamMembers', selectedUserName);
                        setSearchText('');
                        setSearchTerm('');
                      }}
                      input={<OutlinedInput label="Select Team Member*" />}
                      // Add these props to prevent auto-selection
                      autoComplete="off"
                      disableAutoFocus
                      renderValue={(selected) => {
                        return (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                            {selected.map((userName) => {
                              const employee = employeeData.find((emp) => emp.userName === userName);
                              return (
                                <Chip
                                  key={userName}
                                  label={userName}
                                  avatar={<UserAvatar size={24} src={employee?.employeePhotoPath} />}
                                  style={{ margin: 2 }}
                                  onMouseDown={(e) => e.stopPropagation()}
                                  onDelete={(e) => handleDelete(e, userName)}
                                />
                              );
                            })}
                          </div>
                        );
                      }}
                      MenuProps={{
                        PaperProps: {
                          style: {
                            maxHeight: 300,
                            overflowY: 'auto',
                          },
                        },
                        // // Prevent menu from auto-focusing on items
                        // disableAutoFocusItem: true,
                        // // Prevent menu from restoring focus
                        // disableRestoreFocus: true,
                      }}
                    >
                      {/* <ListSubheader> */}
                        <TextField
                          placeholder="Search Team member..."
                          fullWidth
                          value={searchText}
                          onChange={(e) => setSearchText(e.target.value)}
                          onKeyDown={handleSearchKeyDown} // Use the new handler
                          onClick={(e) => e.stopPropagation()}
                          size="small"
                          autoFocus
                          // Add these props for better focus management
                          InputProps={{
                            autoComplete: 'off',
                          }}
                        />
                      {/* </ListSubheader> */}

                      {filteredEmployees.map((option) => (
                        <MenuItem
                          key={option.userName}
                          value={option.userName}
                          dense
                          style={{ paddingTop: 4, paddingBottom: 4 }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            <UserAvatar size={28} src={option?.employeePhotoPath} />
                            <div style={{ marginLeft: 8 }}>
                              <Typography variant="body2" style={{ fontSize: 13, fontWeight: 500 }}>
                                {`${option.firstName} ${option.middleName} ${option.lastName}`}
                              </Typography>
                              <Typography variant="caption" style={{ fontSize: 11, color: '#666' }}>
                                {option.department}
                              </Typography>
                            </div>
                          </div>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {formik.touched.personName && formik.errors.personName && (
                    <Typography variant="body2" color="error">
                      {formik.errors.teamMembers}
                    </Typography>
                  )}
                </Grid>
              </>
            )}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description *"
                name="description"
                type="text"
                variant="outlined"
                multiline
                rows={3}
                value={formik.values.description}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.description && !!formik.errors.description}
                helperText={formik.touched.description && formik.errors.description}
                margin="dense"
                InputLabelProps={{ style: { fontSize: variants.fontSize.SUB_HEADING } }}
                InputProps={{
                  placeholder: 'Enter description here...',
                  style: { fontSize: variants.fontSize.SUB_HEADING },
                }}
              />
            </Grid>
            <Grid item xs={12} sx={{ mt: 1, mb: 1 }}>
              {!isSm ? (
                <FileUpload
                  handleFileChange={handleFileChange}
                  formik={formik}
                  name="attachment"
                  accept={'.jpg,.jpeg,.png'}
                />
              ) : (
                <Button variant="contained" onClick={takePicture}>
                  Upload File
                </Button>
              )}
              {isAttached && (
                <AttachmentNameDisplay attachments={formik.values.attachment} />
              )}
            </Grid>
            <Grid item xs={12}>
              <Button
                type="submit"
                variant="contained"
                fullWidth
                sx={{ fontWeight: 'bold', borderRadius: '8px', fontSize: variants.fontSize.TEXT }}
                ref={nominateButtonRef}
              >
                Nominate
              </Button>
            </Grid>
          </Grid>
        </form>
      </div>
    </div>
  );
};

export default RnRForm;
